import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Eye, TrendingUp, Zap, DollarSign, Users, Clock, FlaskConical, AlertTriangle, Scale } from 'lucide-react';
import GeneralCostPerKgCard from './GeneralCostPerKgCard';

const ReportsSummaryCards = ({
  receptionTotals,
  washingTotals,
  washingAbortedTotals = 0,
  washingAbortedKilos = 0,
  dryingTotals = { kilosSecados: 0, ciclos: 0 },
  dryingAbortedTotals = 0,
  dryingAbortedKilos = 0,
  kilosAlistados = 0,
  washingByMachine,
  washingAbortedByMachine = [],
  programasLavado = [],
  kilosProcesados = 0,
  loadingProg = false,
  productConsumption = [],
  pcLoading = false,
  productosPorPrograma = [],
  utilityTotals,
  utilityTypeTotals,
  workedHours,
  startDate,
  endDate,
  laundryIndicators = null,
}) => {
  const fmtMoney = (n) =>
    `$${new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0))}`;

  const cardBase =
    'relative overflow-hidden bg-white dark:bg-gray-800 shadow-sm rounded-2xl border border-gray-200/80 dark:border-gray-700/80 hover:shadow-lg transition-all duration-300';

  // Estilos tipográficos empresariales compactos
  const labelCls = 'text-xs font-medium text-gray-600 dark:text-gray-400';
  const valuePrimaryCls = 'text-xl font-bold text-gray-900 dark:text-white tabular-nums';
  const valueSecondaryCls = 'text-base font-semibold text-gray-800 dark:text-gray-200 tabular-nums';

  // Calculate number of days in the filter range (calendar days, not timestamp diff)
  const calculateDaysInRange = () => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;

    // Regla 1 (defensiva): si el string ISO empieza con el mismo YYYY-MM-DD,
    // es el mismo día calendario sin importar la hora (UTC midnight vs local
    // midnight vs end-of-day). Esto cubre formatos mezclados por caché vieja.
    const startYMD = String(startDate).slice(0, 10);
    const endYMD = String(endDate).slice(0, 10);
    if (
      /^\d{4}-\d{2}-\d{2}$/.test(startYMD) &&
      startYMD === endYMD
    ) {
      return 1;
    }

    // Regla 2: comparar días calendario (año/mes/día) en hora local para
    // alinear con la zona usada por el data filter (dateRange).
    const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    if (startUTC === endUTC) return 1;

    const diffDays = Math.round((endUTC - startUTC) / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : 1;
  };

  const daysInRange = calculateDaysInRange();
  // Etiqueta compacta: "1" para un solo día, "N días" para múltiples
  const daysLabel = daysInRange === 1 ? '1' : `${daysInRange} días`;
  const dailyRejection = (Number(receptionTotals?.kilosRechazo || 0)) / daysInRange;
  const dailyWashingKg = (Number(washingTotals?.kilosLavados || 0)) / daysInRange;
  const dailyCycles = (Number(washingTotals?.ciclos || 0)) / daysInRange;
  const dailyConsumption = (Number(receptionTotals?.kilosRecibidos || 0)) / daysInRange;
  const dailyDryingKg = (Number(dryingTotals?.kilosSecados || 0)) / daysInRange;
  const dailyDryingCycles = (Number(dryingTotals?.ciclos || 0)) / daysInRange;

  // Datos para la tabla compacta de Ciclos de Lavado (lotes, abortados, sin registrar)
  const washingNominalCiclos = Math.max(Number(washingTotals.ciclos || 0) - Number(washingAbortedTotals || 0), 0);
  const washingNominalKg = Number(washingTotals.kilosLavados || 0);
  const washingAbortadosCiclos = Math.max(Number(washingAbortedTotals || 0), 0);
  const washingAbortadosKg = Number(washingAbortedKilos || 0);
  // Lotes recibidos en la lavandería
  const washingLotesCiclos = Math.max(Number(receptionTotals.conteo || 0), 0);
  const washingLotesKg = Math.max(Number(receptionTotals.kilosProcesados || 0), 0);
  // Regla de trazabilidad: lotes + abortados debe dar el total nominal.
  // - Si total nominal > lotes + abortados => hay lotes sin registrar
  //   (faltaron ciclos por abrir para esos lotes, o se contabilizaron antes de tiempo).
  // - Si lotes + abortados > total nominal => hay ciclos de más
  //   (ciclos sin lote de recepción asociado: entrada manual, ciclo rezagado o desfase).
  const lotesMasAbortados = washingLotesCiclos + washingAbortadosCiclos;
  const washingSinRegistrarCiclos = Math.max(washingNominalCiclos - lotesMasAbortados, 0);
  const washingCiclosDeMas = Math.max(lotesMasAbortados - washingNominalCiclos, 0);
  // Estimación de kg sin registrar usando el promedio de kg por ciclo (del total ciclos)
  const totalCiclosReferencia = Math.max(washingNominalCiclos + washingAbortadosCiclos, 1);
  const kgPorCiclo = (washingNominalKg + washingAbortadosKg) / totalCiclosReferencia;
  const washingSinRegistrarKg = washingSinRegistrarCiclos * kgPorCiclo;
  // Totales y porcentajes (la tabla muestra: Lotes + Abortados + Sin registrar = Total)
  const washingTotalCiclosTabla = washingLotesCiclos + washingAbortadosCiclos + washingSinRegistrarCiclos;
  const washingTotalKgTabla = washingLotesKg + washingAbortadosKg + washingSinRegistrarKg;
  const tablaLotesPct = washingTotalKgTabla > 0 ? (washingLotesKg / washingTotalKgTabla) * 100 : 0;
  const tablaAbortadosPct = washingTotalKgTabla > 0 ? (washingAbortadosKg / washingTotalKgTabla) * 100 : 0;
  const tablaSinRegistrarPct = washingTotalKgTabla > 0 ? (washingSinRegistrarKg / washingTotalKgTabla) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* Costo General por Kg - Tarjeta principal ancho completo */}
      <div className="w-full">
        <GeneralCostPerKgCard
          productConsumption={productConsumption}
          receptionTotals={receptionTotals}
          utilityTotals={utilityTotals}
          utilityTypeTotals={utilityTypeTotals}
          workedHours={workedHours}
          indicators={laundryIndicators}
        />
      </div>

      {/* ── Sección: Indicadores de aprovechamiento de capacidad de lavado ── */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-6 md:p-8">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500" />
        <div className="flex items-center gap-3 mb-8">
          <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 shadow-md shadow-emerald-200 dark:shadow-emerald-900/40">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Indicadores de Aprovechamiento de Capacidad de Lavado</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Recepción, ciclos y programas de lavado</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Recepción */}
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-500" />
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                </div>
                Recepción
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Métricas principales */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/40">
                  <p className={labelCls}>Procesados</p>
                  <p className={valuePrimaryCls}>{receptionTotals.kilosProcesados.toFixed(1)} kg</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">{receptionTotals.conteo} lotes</p>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-green-50 to-orange-50 dark:from-green-900/20 dark:to-orange-900/20 rounded-xl border border-green-100 dark:border-green-800/40">
                  <p className={labelCls}>No lotes</p>
                  <p className={valuePrimaryCls}>{receptionTotals.conteo} </p>
                </div>
                {/*               <div className="text-center p-3 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl border border-red-100 dark:border-red-800/40">
                <p className={labelCls}>Rechazo</p>
                <p className={valuePrimaryCls}>{receptionTotals.kilosRechazo.toFixed(1)} kg</p>
                <p className="text-xs text-red-500 dark:text-red-400">
                  {(receptionTotals.kilosProcesados
                    ? (receptionTotals.kilosRechazo / receptionTotals.kilosProcesados) * 100
                    : 0
                  ).toFixed(1)}%
                </p>
              </div> */}
              </div>

              {/* Estado de procesamiento */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Estado</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Finalizado</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{receptionTotals?.kilosFinalizados?.toFixed(0) || '0.0'}kg ({receptionTotals?.conteo - receptionTotals?.pendientes})</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Pendientes</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{(receptionTotals.kilosProcesados - (receptionTotals?.kilosFinalizados || 0)).toFixed(0)}kg ({receptionTotals.pendientes})</span>
                  </div>
                </div>
              </div>

              {/* Promedios diarios */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Promedios Diarios ({daysLabel})</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Procesados</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{(receptionTotals.kilosProcesados / daysInRange).toFixed(1)} kg</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Rechazo</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{(receptionTotals.kilosRechazo / daysInRange).toFixed(1)} kg</span>
                  </div>
                </div>
              </div>

              {/* Desglose de rechazo */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Causas de Rechazo</p>
                <div className="space-y-1">
                  {[
                    { key: 'arrastre', label: 'Arrastre' },
                    { key: 'cloro', label: 'Amarilla' },
                    { key: 'grasa', label: 'Grasa' },
                    { key: 'tintas', label: 'Tintas' },
                    { key: 'oxido', label: 'Óxido' },
                    { key: 'clorhexidina', label: 'Clorhexidina' },
                    { key: 'pegante', label: 'Pegante' },
                    { key: 'azul_de_metileno', label: 'Azul de metileno' },
                    { key: 'otro', label: 'Otro' },
                  ].map(({ key, label }) => {
                    const kilos = Number(receptionTotals?.[key] || 0);
                    const pctRech = receptionTotals.kilosRechazo
                      ? (kilos / receptionTotals.kilosRechazo) * 100
                      : 0;

                    if (kilos === 0) return null;

                    return (
                      <div key={key} className="flex justify-between items-center py-1 px-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                        <span className="text-gray-700 dark:text-gray-300">{label}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{kilos.toFixed(1)}kg ({pctRech.toFixed(1)}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ciclos de lavado - ancho doble para mostrar participación sin "ver más" */}
          <Card className={`${cardBase} lg:col-span-2`}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                  <Zap className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                </div>
                Ciclos de Lavado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Métricas principales */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800/40">
                  <p className={labelCls}>Nominal</p>
                  <p className={valuePrimaryCls}>{(Number(washingTotals.kilosLavados) || 0).toFixed(1)} kg</p>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/40">
                  <p className={labelCls}>Total Ciclos</p>
                  <p className={valuePrimaryCls}>{(washingTotals.ciclos - washingAbortedTotals) || 0}</p>
                </div>
              </div>

              {/* Abortados - estilo similar a Nominal, ubicado debajo */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 rounded-xl border border-red-100 dark:border-red-800/40">
                  <p className={labelCls}>Abortados (kg)</p>
                  <p className={`${valuePrimaryCls} text-red-600 dark:text-red-400`}>{washingAbortedKilos.toFixed(1)} kg</p>
                  <p className="text-xs text-red-500 dark:text-red-400">
                    {washingTotals.kilosLavados > 0
                      ? `${((washingAbortedKilos / (Number(washingTotals.kilosLavados) + washingAbortedKilos)) * 100).toFixed(1)}% del nominal`
                      : '0% del nominal'}
                  </p>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 rounded-xl border border-red-100 dark:border-red-800/40">
                  <p className={labelCls}>Abortados (ciclos)</p>
                  <p className={`${valuePrimaryCls} text-red-600 dark:text-red-400`}>{washingAbortedTotals || 0}</p>
                  <p className="text-xs text-red-500 dark:text-red-400">
                    {washingTotals.ciclos > 0
                      ? `${((washingAbortedTotals / washingTotals.ciclos) * 100).toFixed(1)}% del total`
                      : '0% del total'}
                  </p>
                </div>

              </div>

              {/* Alertas de trazabilidad: ciclos sin registrar o ciclos de más */}
              {(washingSinRegistrarCiclos > 0 || washingCiclosDeMas > 0) && (
                <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  {washingSinRegistrarCiclos > 0 && (
                    <div
                      className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/20"
                      role="alert"
                    >
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-800 dark:text-amber-200">
                        <p className="font-semibold">
                          {washingSinRegistrarCiclos} {washingSinRegistrarCiclos === 1 ? 'ciclo sin registrar' : 'ciclos sin registrar'}
                        </p>
                        <p className="mt-0.5 text-amber-700 dark:text-amber-300">
                          Hay lotes recibidos que no tienen ciclo (ni nominal ni abortado). Revisa la trazabilidad del periodo.
                        </p>
                      </div>
                    </div>
                  )}
                  {washingCiclosDeMas > 0 && (
                    <div
                      className="flex items-start gap-2 p-3 rounded-lg border border-sky-200 bg-sky-50 dark:border-sky-800/60 dark:bg-sky-900/20"
                      role="alert"
                    >
                      <AlertTriangle className="h-4 w-4 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-sky-800 dark:text-sky-200">
                        <p className="font-semibold">
                          {washingCiclosDeMas} {washingCiclosDeMas === 1 ? 'ciclo de más' : 'ciclos de más'}
                        </p>
                        <p className="mt-0.5 text-sky-700 dark:text-sky-300">
                          Hay ciclos sin un lote de recepción asociado (entrada manual, ciclo rezagado o desfase de fechas).
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Promedios diarios - siempre visible */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Promedios Diarios ({daysLabel})</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Diario kg</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{dailyWashingKg.toFixed(1)} kg</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Diario ciclos</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{dailyCycles.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              {/* Tabla compacta de ciclos y kilos por tipo - fácil de leer */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Ciclos</p>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-medium">Tipo</th>
                        <th className="text-right px-3 py-1.5 font-medium">(ciclos)</th>
                        <th className="text-right px-3 py-1.5 font-medium">(kg)</th>
                        <th className="text-right px-3 py-1.5 font-medium">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      <tr>
                        <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">Lotes</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-gray-900 dark:text-white tabular-nums">
                          {washingLotesCiclos}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-gray-900 dark:text-white tabular-nums">
                          {washingLotesKg.toFixed(1)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                          {tablaLotesPct.toFixed(1)}%
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1.5 text-red-600 dark:text-red-400">Abortados</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-red-600 dark:text-red-400 tabular-nums">
                          {washingAbortadosCiclos}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-red-600 dark:text-red-400 tabular-nums">
                          {washingAbortadosKg.toFixed(1)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-red-500 dark:text-red-400 tabular-nums">
                          {tablaAbortadosPct.toFixed(1)}%
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">Sin registrar</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-gray-900 dark:text-white tabular-nums">
                          {washingSinRegistrarCiclos}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-gray-900 dark:text-white tabular-nums">
                          {washingSinRegistrarKg.toFixed(1)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                          {tablaSinRegistrarPct.toFixed(1)}%
                        </td>
                      </tr>
                    </tbody>
                    {washingTotalCiclosTabla > 0 && (
                      <tfoot className="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-600 dark:text-gray-300 font-medium">
                        <tr>
                          <td className="px-3 py-1.5">Total</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{washingTotalCiclosTabla}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{washingTotalKgTabla.toFixed(1)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">100.0%</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* Participación por máquina - ciclos nominales (siempre visible) */}
              {Array.isArray(washingByMachine) && washingByMachine.length > 0 && (() => {
                // Preparar datos (top 7 + "Otros") para mantener la torta legible
                const list = (washingByMachine || []).slice();
                const sorted = list.sort((a, b) => Number(b.pctKilos || 0) - Number(a.pctKilos || 0));
                const top = sorted.slice(0, 7);
                const rest = sorted.slice(7);
                const restPct = rest.reduce((acc, it) => acc + Number(it.pctKilos || 0), 0);
                const restKilos = rest.reduce((acc, it) => acc + Number(it.kilosLavados || 0), 0);
                const data = rest.length > 0 ? [...top, { id: 'otros', nombre: 'Otros', pctKilos: restPct, kilosLavados: restKilos }] : top;

                const sortedC = list.slice().sort((a, b) => Number(b.ciclos || 0) - Number(a.ciclos || 0));
                const topC = sortedC.slice(0, 7);
                const restC = sortedC.slice(7);
                const totalCiclos = list.reduce((sum, it) => sum + Number(it.ciclos || 0), 0) || 0;
                const restCiclos = restC.reduce((acc, it) => acc + Number(it.ciclos || 0), 0);
                const dataC = restC.length > 0 ? [...topC, { id: 'otros', nombre: 'Otros', ciclos: restCiclos }] : topC;

                const colors = [
                  '#3b82f6', '#10b981', '#f59e0b', '#7c3aed', '#ef4444', '#14b8a6', '#a855f7', '#94a3b8',
                ];
                const idOf = (d) => (d && (d.id ?? d.nombre)) ?? '';
                const unionTop = [];
                const pushUnique = (arr, item) => {
                  const key = idOf(item);
                  if (key && !arr.some((x) => idOf(x) === key)) arr.push(item);
                };
                top.forEach((it) => pushUnique(unionTop, it));
                topC.forEach((it) => pushUnique(unionTop, it));
                const colorMap = {};
                unionTop.slice(0, 7).forEach((it, idx) => {
                  const key = idOf(it);
                  if (key) colorMap[key] = colors[idx];
                });

                const buildSlices = (inputData, valueKey, useColorMap) => {
                  const r = 42;
                  const C = 2 * Math.PI * r;
                  let acc = 0;
                  return inputData.map((d, i) => {
                    const pct = valueKey === 'pctKilos'
                      ? Math.max(0, Number(d.pctKilos || 0))
                      : (totalCiclos > 0 ? Math.max(0, (Number(d.ciclos || 0) / totalCiclos) * 100) : 0);
                    const len = (pct / 100) * C;
                    const slice = {
                      key: `${valueKey}-${d.id || i}`,
                      label: d?.nombre || d?.id || 'Sin máquina',
                      pct,
                      kilos: Number(d.kilosLavados || 0),
                      ciclos: Number(d.ciclos || 0),
                      color: (d.id === 'otros' || d.nombre === 'Otros')
                        ? '#94a3b8'
                        : (useColorMap[idOf(d)] || colors[Math.min(i, colors.length - 1)]),
                      dasharray: `${len} ${C - len}`,
                      dashoffset: -acc,
                    };
                    acc += len;
                    return slice;
                  });
                };

                const slicesKilos = buildSlices(data, 'pctKilos', colorMap);
                const slicesCiclos = buildSlices(dataC, 'ciclos', colorMap);

                if (!slicesKilos.length && !slicesCiclos.length) return null;

                return (
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className={labelCls}>Participación por Máquina (Nominales)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Donut por kilos */}
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-[11px] text-gray-600 dark:text-gray-400 font-medium">Por kilos</div>
                        <svg width="130" height="130" viewBox="0 0 100 100">
                          <g transform="rotate(-90 50 50)">
                            {slicesKilos.map((s) => (
                              <circle key={s.key} cx="50" cy="50" r={42} fill="transparent"
                                stroke={s.color} strokeWidth="12"
                                strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset} />
                            ))}
                          </g>
                          <circle cx="50" cy="50" r="30" fill="white" />
                          <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#334155">
                            {slicesKilos.reduce((a, b) => a + b.kilos, 0).toLocaleString()} kg
                          </text>
                        </svg>
                        <div className="w-full divide-y divide-gray-100 dark:divide-gray-700">
                          {slicesKilos.map((s) => (
                            <div key={s.key} className="flex items-center justify-between py-1 text-xs">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></span>
                                <span className="truncate" title={s.label}>{s.label}</span>
                              </div>
                              <div className="text-right text-gray-700 dark:text-gray-300 tabular-nums">
                                <span className="font-medium">{s.pct.toFixed(1)}%</span>
                                <span className="text-gray-500 ml-1">· {s.kilos.toLocaleString()} kg</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Donut por ciclos */}
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-[11px] text-gray-600 dark:text-gray-400 font-medium">Por ciclos</div>
                        <svg width="130" height="130" viewBox="0 0 100 100">
                          <g transform="rotate(-90 50 50)">
                            {slicesCiclos.map((s) => (
                              <circle key={s.key} cx="50" cy="50" r={42} fill="transparent"
                                stroke={s.color} strokeWidth="12"
                                strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset} />
                            ))}
                          </g>
                          <circle cx="50" cy="50" r="30" fill="white" />
                          <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#334155">
                            {totalCiclos.toLocaleString()} ciclos
                          </text>
                        </svg>
                        <div className="w-full divide-y divide-gray-100 dark:divide-gray-700">
                          {slicesCiclos.map((s) => (
                            <div key={s.key} className="flex items-center justify-between py-1 text-xs">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></span>
                                <span className="truncate" title={s.label}>{s.label}</span>
                              </div>
                              <div className="text-right text-gray-700 dark:text-gray-300 tabular-nums">
                                <span className="font-medium">{s.pct.toFixed(1)}%</span>
                                <span className="text-gray-500 ml-1">· {s.ciclos.toLocaleString()} ciclos</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Participación de ciclos ABORTADOS por máquina */}
              {Array.isArray(washingAbortedByMachine) && washingAbortedByMachine.length > 0 && (() => {
                const list = (washingAbortedByMachine || []).slice();
                const sorted = list.sort((a, b) => Number(b.pctKilos || 0) - Number(a.pctKilos || 0));
                const top = sorted.slice(0, 7);
                const rest = sorted.slice(7);
                const restPct = rest.reduce((acc, it) => acc + Number(it.pctKilos || 0), 0);
                const restKilos = rest.reduce((acc, it) => acc + Number(it.kilosLavados || 0), 0);
                const data = rest.length > 0 ? [...top, { id: 'otros', nombre: 'Otros', pctKilos: restPct, kilosLavados: restKilos }] : top;
                const totalKilosAborted = list.reduce((s, it) => s + Number(it.kilosLavados || 0), 0);
                const totalCiclosAborted = list.reduce((s, it) => s + Number(it.ciclos || 0), 0);

                const colorsAborted = [
                  '#ef4444', '#f97316', '#eab308', '#dc2626', '#fb923c', '#facc15', '#b91c1c', '#94a3b8',
                ];
                const idOf = (d) => (d && (d.id ?? d.nombre)) ?? '';
                const r = 42;
                const C = 2 * Math.PI * r;
                let acc = 0;
                const slices = data.map((d, i) => {
                  const pct = Math.max(0, Number(d.pctKilos || 0));
                  const len = (pct / 100) * C;
                  const slice = {
                    key: `ab-${d.id || i}`,
                    label: d?.nombre || d?.id || 'Sin máquina',
                    pct,
                    kilos: Number(d.kilosLavados || 0),
                    ciclos: Number(d.ciclos || 0),
                    color: (d.id === 'otros' || d.nombre === 'Otros')
                      ? '#94a3b8'
                      : colorsAborted[Math.min(i, colorsAborted.length - 1)],
                    dasharray: `${len} ${C - len}`,
                    dashoffset: -acc,
                  };
                  acc += len;
                  return slice;
                });

                if (!slices.length) return null;
                return (
                  <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className={labelCls}>Participación de Abortados por Máquina</p>
                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4 items-start">
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-[11px] text-red-600 dark:text-red-400 font-medium">Abortados</div>
                        <svg width="120" height="120" viewBox="0 0 100 100">
                          <g transform="rotate(-90 50 50)">
                            {slices.map((s) => (
                              <circle key={s.key} cx="50" cy="50" r={42} fill="transparent"
                                stroke={s.color} strokeWidth="12"
                                strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset} />
                            ))}
                          </g>
                          <circle cx="50" cy="50" r="30" fill="white" />
                          <text x="50" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="7" fill="#334155">
                            {totalCiclosAborted.toLocaleString()} ciclos
                          </text>
                          <text x="50" y="58" textAnchor="middle" dominantBaseline="middle" fontSize="6" fill="#64748b">
                            {totalKilosAborted.toLocaleString()} kg
                          </text>
                        </svg>
                      </div>
                      <div className="w-full divide-y divide-gray-100 dark:divide-gray-700">
                        {slices.map((s) => (
                          <div key={s.key} className="flex items-center justify-between py-1 text-xs">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></span>
                              <span className="truncate" title={s.label}>{s.label}</span>
                            </div>
                            <div className="text-right text-gray-700 dark:text-gray-300 tabular-nums">
                              <span className="font-medium text-red-600 dark:text-red-400">{s.pct.toFixed(1)}%</span>
                              <span className="text-gray-500 ml-1">· {s.ciclos} ciclos · {s.kilos.toLocaleString()} kg</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Ciclos de secado */}
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-amber-50 dark:bg-amber-900/30">
                  <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                Ciclos de Secado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-100 dark:border-amber-800/40">
                  <p className={labelCls}>Kilos Secados</p>
                  <p className={valuePrimaryCls}>{(Number(dryingTotals.kilosSecados) || 0).toFixed(1)} kg</p>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl border border-orange-100 dark:border-orange-800/40">
                  <p className={labelCls}>Total Ciclos</p>
                  <p className={valuePrimaryCls}>{dryingTotals.ciclos || 0}</p>
                </div>
              </div>

              {/* Promedios diarios */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Promedios Diarios ({daysLabel})</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Diario kg</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{dailyDryingKg.toFixed(1)} kg</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Diario ciclos</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{dailyDryingCycles.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              {/* Análisis de eficiencia */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Análisis de Eficiencia</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Eficiencia</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {(dryingTotals.kilosSecados
                        ? (kilosAlistados / dryingTotals.kilosSecados) * 100
                        : 0
                      ).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Abortados */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Abortados</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Ciclos</span>
                    <span className="font-semibold text-red-500 dark:text-red-400">{dryingAbortedTotals}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Kilos</span>
                    <span className="font-semibold text-red-500 dark:text-red-400">{dryingAbortedKilos.toFixed(1)} kg</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">% del total</span>
                    <span className="font-semibold text-red-500 dark:text-red-400">
                      {dryingTotals.ciclos > 0 ? ((dryingAbortedTotals / dryingTotals.ciclos) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Participación por Programa - fila propia a ancho completo */}
        <Card className={cardBase}>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-400 to-purple-500" />
          <CardHeader className="pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-violet-50 dark:bg-violet-900/30">
                <Users className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
              </div>
              Programas de Lavado
            </CardTitle>
            <Popover>
              <PopoverTrigger asChild>
                <button className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs hover:text-gray-900 dark:hover:text-white transition-colors">
                  <Eye className="h-4 w-4" /> Ver detalle
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[520px] max-h-80 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    Detalle por Programa
                  </div>
                  <div className="grid grid-cols-12 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 pb-1">
                    <span className="col-span-4">Programa</span>
                    <span className="col-span-2 text-right">Cargado</span>
                    <span className="col-span-2 text-right">Peso FC</span>
                    <span className="col-span-2 text-right">%</span>
                    <span className="col-span-2 text-right">Ciclos</span>
                  </div>
                  <div className="space-y-2">
                    {(programasLavado || []).map((p, idx) => {
                      const kilos = Number(p.kilos || 0);
                      const pesoFC = Number(p.peso_total_factor_Carga || 0);
                      const pct = kilosProcesados > 0 ? (kilos / kilosProcesados) * 100 : 0;
                      return (
                        <div key={idx} className="grid grid-cols-12 items-center text-xs gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <span className="col-span-4 truncate text-gray-900 dark:text-white" title={p.nombre}>{p.nombre}</span>
                          <span className="col-span-2 text-right font-mono font-semibold text-gray-900 dark:text-white">{kilos.toLocaleString()} kg</span>
                          <span className="col-span-2 text-right font-mono text-indigo-700 dark:text-indigo-400">
                            {pesoFC > 0 ? `${pesoFC.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg` : '—'}
                          </span>
                          <span className="col-span-2 text-right text-gray-500">
                            {pct.toFixed(1)}%
                          </span>
                          <span className="col-span-2 text-right text-gray-600 dark:text-gray-400">
                            {Number(p.count || 0)} ciclos
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(loadingProg ? [] : (programasLavado || []).slice(0, 8)).map((p, idx) => {
                const kilos = Number(p.kilos || 0);
                const pct = kilosProcesados > 0 ? (kilos / kilosProcesados) * 100 : 0;
                // Paleta de colores vibrantes para las barras de programas
                const programColors = [
                  '#3b82f6', // blue-500
                  '#10b981', // emerald-500
                  '#f59e0b', // amber-500
                  '#ef4444', // red-500
                  '#8b5cf6', // violet-500
                  '#ec4899', // pink-500
                  '#14b8a6', // teal-500
                  '#f97316', // orange-500
                ];
                const barColor = programColors[idx % programColors.length];

                return (
                  <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between text-sm font-medium mb-2">
                      <span className="truncate text-gray-900 dark:text-white" title={p.nombre}>{p.nombre}</span>
                      <span className="text-gray-600 dark:text-gray-400">{pct.toFixed(1)}%</span>
                    </div>
                    <div className="mb-2 h-2 w-full rounded bg-gray-200 dark:bg-gray-600">
                      <div
                        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }}
                        className="h-2 rounded transition-all duration-300"
                      ></div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{kilos.toLocaleString()} kg</span>
                        {(() => {
                          const pesoFC = Number(p.peso_total_factor_Carga || 0);
                          if (!Number.isFinite(pesoFC) || pesoFC <= 0) return null;
                          const diffKg = kilos - pesoFC;
                          const diffPct = pesoFC > 0 ? (diffKg / pesoFC) * 100 : 0;
                          const diffSign = diffKg > 0 ? '+' : '';
                          const diffTone = Math.abs(diffKg) < 0.5
                            ? 'text-green-700 dark:text-green-400 border-green-200 dark:border-green-700/50'
                            : diffKg > 0
                              ? 'text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-700/50'
                              : 'text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700/50';
                          return (
                            <span
                              title={`Peso por factor de carga (capacidad × 10 / factorCarga) — diferencia vs cargado: ${diffSign}${diffKg.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg (${diffSign}${diffPct.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%)`}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-white dark:bg-gray-800 font-mono ${diffTone}`}
                            >
                              <Scale className="h-3 w-3" />
                              <span>{pesoFC.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg FC</span>
                            </span>
                          );
                        })()}
                      </div>
                      <span className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                        {Number(p.count || 0)} ciclos
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {loadingProg && (
              <div className="text-center py-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Calculando...</span>
              </div>
            )}
            {!loadingProg && (programasLavado || []).length === 0 && (
              <div className="text-center py-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Sin datos disponibles</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Sección: Servicios Públicos ── */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-6 md:p-8">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-red-600 shadow-md shadow-amber-200 dark:shadow-amber-900/40">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Servicios Públicos</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Costos y consumos de agua, gas y electricidad</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
          {/* Servicios (Total) */}
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-amber-50 dark:bg-amber-900/30">
                  <DollarSign className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                Servicios Públicos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Métricas principales */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-100 dark:border-amber-800/40">
                  <p className={labelCls}>Costo Total</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{fmtMoney(utilityTotals.costoTotal)}</p>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl border border-orange-100 dark:border-orange-800/40">
                  <p className={labelCls}>Costo por Kg</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {fmtMoney((utilityTotals.costoTotal) / (receptionTotals.kilosProcesados || 1))}
                  </p>
                </div>
              </div>

              {/* Análisis de eficiencia compacto */}
              <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className={labelCls}>Análisis de Eficiencia</p>
                <div className="space-y-1">
                  <div className="flex justify-between items-center py-1 px-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Óptimo/kg</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {fmtMoney((utilityTotals.costoTotal) / (washingTotals.kilosLavados || 1))}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1 px-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total óptimo</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {fmtMoney(((utilityTotals.costoTotal) / (washingTotals.kilosLavados || 1)) * receptionTotals.kilosProcesados)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1 px-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Ineficiencia</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {fmtMoney(Math.abs(((utilityTotals.costoTotal) / (washingTotals.kilosLavados || 1)) * receptionTotals.kilosProcesados - utilityTotals.costoTotal))}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Servicios por tipo */}
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-blue-500" />
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-cyan-50 dark:bg-cyan-900/30">
                  <Zap className="h-3.5 w-3.5 text-cyan-600 dark:text-cyan-400" />
                </div>
                Desglose de Servicios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: 'agua', label: 'Agua', unit: 'L' },
                { key: 'aguaCaliente', label: 'Agua Caliente', unit: 'L' },
                { key: 'gas', label: 'Gas', unit: 'm³' },
                { key: 'Electricidad', label: 'Electricidad', unit: 'kWh' },
              ].map(({ key, label, unit }) => {
                const item = utilityTypeTotals[key] || {};
                return (
                  <div key={key} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">{label}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Consumo</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{item.consumoLitros?.toFixed(0)} {unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Costo</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{fmtMoney(item.costos)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Por kg</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {(item.consumoLitros / (receptionTotals.kilosProcesados || 1)).toFixed(2)} {unit}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Costo/kg</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {fmtMoney((item.costos) / (receptionTotals.kilosProcesados || 1))}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Consumo de Productos */}
        </div>
      </div>

      {/* ── Sección: Análisis de Productos (Consumo real vs Estimado vs Diferencia) ── */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-6 md:p-8">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-teal-500 to-orange-500" />
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-orange-600 shadow-md shadow-pink-200 dark:shadow-pink-900/40">
            <FlaskConical className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Análisis de Productos</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Consumo real (inventario) vs estimado (parámetros) y su desviación</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-400 to-rose-500" />
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-pink-50 dark:bg-pink-900/30">
                  <Clock className="h-3.5 w-3.5 text-pink-600 dark:text-pink-400" />
                </div>
                Consumo de Productos (inventario)
              </CardTitle>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs hover:text-gray-900 dark:hover:text-white transition-colors">
                    <Eye className="h-4 w-4" /> Ver detalle
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[480px] max-h-80 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Detalle por Producto
                    </div>
                    <div className="grid grid-cols-5 text-xs font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 pb-2">
                      <span className="col-span-2">Producto</span>
                      <span className="text-right">Litros</span>
                      <span className="text-right">Costo</span>
                      <span className="text-right">%</span>
                    </div>
                    <div className="space-y-1">
                      {(() => {
                        const items = Array.isArray(productConsumption) ? productConsumption : [];
                        const totalLitros = items.reduce((acc, it) => acc + Number(it?.totalUsado || 0), 0);
                        const sorted = items.slice().sort((a, b) => Number(b.totalUsado || 0) - Number(a.totalUsado || 0));
                        return sorted.map((p, idx) => {
                          const litros = Number(p?.totalUsado || 0);
                          const costo = Number(p?.totalCosto || 0);
                          const pct = totalLitros > 0 ? (litros / totalLitros) * 100 : 0;
                          return (
                            <div key={p?.producto?.id || idx} className="grid grid-cols-5 items-center text-xs gap-2 py-2 px-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <span className="col-span-2 truncate text-gray-900 dark:text-white" title={p?.producto?.nombre}>{p?.producto?.nombre}</span>
                              <div className="text-right font-semibold text-gray-900 dark:text-white">{litros.toLocaleString()} L</div>
                              <div className="text-right text-gray-600 dark:text-gray-400">
                                {costo > 0 ? costo.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }) : '-'}
                              </div>
                              <div className="text-right text-gray-500">{pct.toFixed(1)}%</div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const items = Array.isArray(productConsumption) ? productConsumption : [];
                const totalLitros = items.reduce((acc, it) => acc + Number(it?.totalUsado || 0), 0);
                const totalCosto = items.reduce((acc, it) => acc + Number(it?.totalCosto || 0), 0);
                // Estandarizado: ambos cards reportan per-kg en ml/kg.
                // totalUsado viene en L → convertimos a ml multiplicando por 1000.
                const kilosProcesados = Number(receptionTotals?.kilosProcesados || 0) || 1;
                const mlPorKg = (totalLitros * 1000) / kilosProcesados;
                const costoPorKg = (totalCosto / kilosProcesados);
                const sorted = items.slice().sort((a, b) => Number(b.totalUsado || 0) - Number(a.totalUsado || 0));
                const top = sorted.slice(0, 6);
                // Paleta de colores vibrantes para productos
                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                return (
                  <>
                    {/* Métricas principales compactas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className={labelCls}>Total Litros</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{totalLitros.toFixed(1)}</p>
                        <p className="text-xs text-gray-500">L</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className={labelCls}>ml por Kg</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{mlPorKg.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">ml/kg</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className={labelCls}>Costo Total</p>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {totalCosto.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className={labelCls}>Costo/Kg</p>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {costoPorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>

                    {/* Barra de distribución compacta */}
                    <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className={labelCls}>Distribución de Productos</p>
                      <div className="w-full h-3 rounded overflow-hidden bg-gray-200 dark:bg-gray-600">
                        <div className="flex w-full h-full">
                          {(pcLoading ? [] : top).map((p, idx) => {
                            const litros = Number(p?.totalUsado || 0);
                            const pct = totalLitros > 0 ? (litros / totalLitros) * 100 : 0;
                            return (
                              <div
                                key={p?.producto?.id || idx}
                                title={`${p?.producto?.nombre} · ${litros.toFixed(1)} L (${pct.toFixed(1)}%)`}
                                style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }}
                                className="h-full"
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Lista de productos principales compacta */}
                    <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className={labelCls}>Productos Principales</p>
                      <div className="space-y-1">
                        {pcLoading && (
                          <div className="text-center py-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Calculando...</span>
                          </div>
                        )}
                        {!pcLoading && items.length === 0 && (
                          <div className="text-center py-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Sin datos disponibles</span>
                          </div>
                        )}
                        {!pcLoading && top.map((p, idx) => {
                          const litros = Number(p?.totalUsado || 0);
                          const costo = Number(p?.totalCosto || 0);
                          const pct = totalLitros > 0 ? (litros / totalLitros) * 100 : 0;
                          return (
                            <div key={p?.producto?.id || idx} className="flex items-center justify-between py-1 px-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                <span className="truncate text-gray-900 dark:text-white" title={p?.producto?.nombre}>{p?.producto?.nombre}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">{pct.toFixed(1)}%</span>
                                <div className="text-right">
                                  <div className="font-semibold text-gray-900 dark:text-white">{litros.toFixed(0)}L</div>
                                  {costo > 0 && (
                                    <div className="text-gray-600 dark:text-gray-400">
                                      {costo.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Productos Estimados por Programa */}
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-cyan-500" />
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-teal-50 dark:bg-teal-900/30">
                  <FlaskConical className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
                </div>
                Productos Estimados por Programa
              </CardTitle>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs hover:text-gray-900 dark:hover:text-white transition-colors">
                    <Eye className="h-4 w-4" /> Ver detalle
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[520px] max-h-96 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Estimado por Programa y Producto
                    </div>
                    {(() => {
                      const items = Array.isArray(productosPorPrograma) ? productosPorPrograma : [];
                      const totalGeneralMl = items.reduce((acc, p) => acc + Number(p.totalMl || 0), 0);
                      const totalGeneralL = totalGeneralMl / 1000;
                      if (items.length === 0) {
                        return <div className="text-xs text-gray-500 text-center py-4">Sin datos disponibles</div>;
                      }
                      return items.map((prog, idx) => {
                        const totalMl = Number(prog.totalMl || 0);
                        const totalL = totalMl / 1000;
                        const pct = totalGeneralMl > 0 ? (totalMl / totalGeneralMl) * 100 : 0;
                        return (
                          <div key={prog.programaId || idx} className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-semibold text-gray-900 dark:text-white truncate" title={prog.programaNombre}>
                                {prog.programaNombre}
                              </span>
                              <div className="text-right">
                                <span className="font-semibold text-teal-700 dark:text-teal-400">{totalL.toFixed(2)} L</span>
                                <span className="text-gray-500 ml-2">({pct.toFixed(1)}%)</span>
                              </div>
                            </div>
                            <div className="space-y-1 pl-2">
                              {(prog.productos || []).map((prod, jdx) => {
                                const prodMl = Number(prod.totalMl || 0);
                                return (
                                  <div key={prod.id || jdx} className="flex items-center justify-between text-xs text-gray-700 dark:text-gray-300">
                                    <span className="truncate" title={prod.nombre}>{prod.nombre}</span>
                                    <span className="font-mono">{(prodMl / 1000).toFixed(2)} L</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </PopoverContent>
              </Popover>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const items = Array.isArray(productosPorPrograma) ? productosPorPrograma : [];
                // Estandarizado: totales en litros, per-kg en ml/kg.
                // Backend devuelve totalMl en ml → dividimos por 1000 para mostrar en L.
                const totalGeneralMl = items.reduce((acc, p) => acc + Number(p.totalMl || 0), 0);
                const totalGeneralL = totalGeneralMl / 1000;
                const kilosProcesados = Number(receptionTotals?.kilosProcesados || 0) || 1;
                const mlPorKg = totalGeneralMl / kilosProcesados;
                const top = items.slice(0, 6);
                const colors = ['#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b'];

                if (loadingProg) {
                  return <div className="text-center py-4"><span className="text-sm text-gray-500">Calculando...</span></div>;
                }
                if (items.length === 0) {
                  return (
                    <div className="text-center py-4 space-y-2">
                      <p className="text-sm text-gray-500">Sin datos disponibles</p>
                      <p className="text-xs text-gray-400">Las recepciones deben tener `productosUsados` poblado (usa el botón "Recalcular Productos" en el tablero de recepción).</p>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Métricas principales */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl border border-teal-100 dark:border-teal-800/40">
                        <p className={labelCls}>Total Estimado</p>
                        <p className={valuePrimaryCls}>{totalGeneralL.toFixed(2)} L</p>
                        <p className="text-xs text-teal-600 dark:text-teal-400">{items.length} programas</p>
                      </div>
                      <div className="text-center p-3 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-100 dark:border-cyan-800/40">
                        <p className={labelCls}>ml por Kg</p>
                        <p className={valuePrimaryCls}>{mlPorKg.toFixed(2)}</p>
                        <p className="text-xs text-cyan-600 dark:text-cyan-400">ml/kg procesado</p>
                      </div>
                    </div>

                    {/* Barra de distribución por programa */}
                    <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className={labelCls}>Distribución por Programa</p>
                      <div className="w-full h-3 rounded overflow-hidden bg-gray-200 dark:bg-gray-600">
                        <div className="flex w-full h-full">
                          {top.map((p, idx) => {
                            const ml = Number(p.totalMl || 0);
                            const pct = totalGeneralMl > 0 ? (ml / totalGeneralMl) * 100 : 0;
                            return (
                              <div
                                key={p.programaId || idx}
                                title={`${p.programaNombre} · ${(ml / 1000).toFixed(2)} L (${pct.toFixed(1)}%)`}
                                style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }}
                                className="h-full"
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Lista compacta por programa con sus top-3 productos */}
                    <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className={labelCls}>Programas principales</p>
                      <div className="space-y-1">
                        {top.map((p, idx) => {
                          const ml = Number(p.totalMl || 0);
                          const pct = totalGeneralMl > 0 ? (ml / totalGeneralMl) * 100 : 0;
                          const subProducts = (p.productos || []).slice(0, 3);
                          return (
                            <div key={p.programaId || idx} className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                  <span className="truncate font-medium text-gray-900 dark:text-white" title={p.programaNombre}>{p.programaNombre}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">{pct.toFixed(1)}%</span>
                                  <span className="font-semibold text-teal-700 dark:text-teal-400">{(ml / 1000).toFixed(2)} L</span>
                                </div>
                              </div>
                              {subProducts.length > 0 && (
                                <div className="mt-1 pl-4 space-y-0.5">
                                  {subProducts.map((prod, jdx) => (
                                    <div key={prod.id || jdx} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                                      <span className="truncate" title={prod.nombre}>{prod.nombre}</span>
                                      <span className="font-mono">{(Number(prod.totalMl || 0) / 1000).toFixed(2)} L</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Diferencia: Consumo (real) vs Estimado */}
          <Card className={cardBase}>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-red-500" />
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-orange-50 dark:bg-orange-900/30">
                  <AlertTriangle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                </div>
                Diferencia: Consumo vs Estimado
              </CardTitle>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs hover:text-gray-900 dark:hover:text-white transition-colors">
                    <Eye className="h-4 w-4" /> Ver detalle
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[560px] max-h-96 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Diferencia por Producto (Real − Estimado)
                    </div>
                    {(() => {
                      // Aplanar productosPorPrograma → estimadoPorProducto (en ml)
                      const estimadoPorProducto = {};
                      (productosPorPrograma || []).forEach((prog) => {
                        (prog.productos || []).forEach((p) => {
                          const id = p.id || p.producto?.id;
                          if (!id) return;
                          if (!estimadoPorProducto[id]) {
                            estimadoPorProducto[id] = {
                              id,
                              nombre: p.nombre || p.producto?.nombre || 'Producto',
                              totalMl: 0,
                            };
                          }
                          estimadoPorProducto[id].totalMl += Number(p.totalMl || 0);
                        });
                      });

                      // Unión de IDs (reales + estimados)
                      const allIds = new Set([
                        ...(productConsumption || []).map((p) => p?.producto?.id).filter(Boolean),
                        ...Object.keys(estimadoPorProducto),
                      ]);

                      const diffs = Array.from(allIds).map((id) => {
                        const real = (productConsumption || []).find((p) => p?.producto?.id === id);
                        const est = estimadoPorProducto[id];
                        const realL = real ? Number(real.totalUsado || 0) : 0;
                        const estimadoL = est ? Number(est.totalMl || 0) / 1000 : 0;
                        const diffL = realL - estimadoL;
                        const pct = estimadoL > 0 ? (diffL / estimadoL) * 100 : null;
                        return {
                          id,
                          nombre: real?.producto?.nombre || est?.nombre || 'Producto',
                          realL,
                          estimadoL,
                          diffL,
                          pct,
                        };
                      }).sort((a, b) => Math.abs(b.diffL) - Math.abs(a.diffL));

                      if (diffs.length === 0) {
                        return <div className="text-xs text-gray-500 text-center py-4">Sin datos comparables</div>;
                      }

                      return diffs.map((d, idx) => {
                        const isOver = d.diffL > 0.001;
                        const isUnder = d.diffL < -0.001;
                        const tone = isOver ? 'text-red-600 dark:text-red-400' : isUnder ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400';
                        const sign = isOver ? '+' : '';
                        return (
                          <div key={d.id || idx} className="grid grid-cols-12 items-center text-xs gap-2 py-2 px-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <span className="col-span-4 truncate text-gray-900 dark:text-white" title={d.nombre}>{d.nombre}</span>
                            <span className="col-span-2 text-right font-mono text-pink-700 dark:text-pink-400">{d.realL.toFixed(2)} L</span>
                            <span className="col-span-2 text-right font-mono text-teal-700 dark:text-teal-400">{d.estimadoL.toFixed(2)} L</span>
                            <span className={`col-span-2 text-right font-mono font-semibold ${tone}`}>
                              {sign}{d.diffL.toFixed(2)} L
                            </span>
                            <span className={`col-span-2 text-right ${tone}`}>
                              {d.pct === null ? '—' : `${sign}${d.pct.toFixed(1)}%`}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </PopoverContent>
              </Popover>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const kilosProcesados = Number(receptionTotals?.kilosProcesados || 0) || 1;

                // Totales reales (en L) y estimados (en ml → L)
                const realTotalL = (productConsumption || []).reduce(
                  (acc, it) => acc + Number(it?.totalUsado || 0),
                  0
                );
                const estimadoTotalMl = (productosPorPrograma || []).reduce(
                  (acc, p) => acc + Number(p.totalMl || 0),
                  0
                );
                const estimadoTotalL = estimadoTotalMl / 1000;

                const diffTotalL = realTotalL - estimadoTotalL;
                const diffMlPorKg = (realTotalL * 1000) / kilosProcesados - estimadoTotalMl / kilosProcesados;
                const diffPct = estimadoTotalL > 0 ? (diffTotalL / estimadoTotalL) * 100 : null;

                // Aplanar estimado por producto
                const estimadoPorProducto = {};
                (productosPorPrograma || []).forEach((prog) => {
                  (prog.productos || []).forEach((p) => {
                    const id = p.id || p.producto?.id;
                    if (!id) return;
                    if (!estimadoPorProducto[id]) {
                      estimadoPorProducto[id] = {
                        id,
                        nombre: p.nombre || p.producto?.nombre || 'Producto',
                        totalMl: 0,
                      };
                    }
                    estimadoPorProducto[id].totalMl += Number(p.totalMl || 0);
                  });
                });

                const allIds = new Set([
                  ...(productConsumption || []).map((p) => p?.producto?.id).filter(Boolean),
                  ...Object.keys(estimadoPorProducto),
                ]);

                const diffs = Array.from(allIds).map((id) => {
                  const real = (productConsumption || []).find((p) => p?.producto?.id === id);
                  const est = estimadoPorProducto[id];
                  const realL = real ? Number(real.totalUsado || 0) : 0;
                  const estimadoL = est ? Number(est.totalMl || 0) / 1000 : 0;
                  const diffL = realL - estimadoL;
                  const pct = estimadoL > 0 ? (diffL / estimadoL) * 100 : null;
                  return {
                    id,
                    nombre: real?.producto?.nombre || est?.nombre || 'Producto',
                    realL,
                    estimadoL,
                    diffL,
                    pct,
                  };
                }).sort((a, b) => Math.abs(b.diffL) - Math.abs(a.diffL));

                const isOver = diffTotalL > 0.001;
                const isUnder = diffTotalL < -0.001;
                const isBalanced = !isOver && !isUnder;

                const tone = isBalanced
                  ? 'green'
                  : isOver
                    ? 'red'
                    : 'blue';
                const toneClasses = {
                  green: { bg: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20', border: 'border-green-100 dark:border-green-800/40', text: 'text-green-700 dark:text-green-400', textStrong: 'text-green-900 dark:text-green-100' },
                  red: { bg: 'from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20', border: 'border-red-100 dark:border-red-800/40', text: 'text-red-600 dark:text-red-400', textStrong: 'text-red-900 dark:text-red-100' },
                  blue: { bg: 'from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20', border: 'border-blue-100 dark:border-blue-800/40', text: 'text-blue-600 dark:text-blue-400', textStrong: 'text-blue-900 dark:text-blue-100' },
                };
                const t = toneClasses[tone];

                const hasData = realTotalL > 0 || estimadoTotalL > 0;
                const topDiffs = diffs.slice(0, 6);

                if (!hasData) {
                  return (
                    <div className="text-center py-4 space-y-2">
                      <p className="text-sm text-gray-500">Sin datos comparables</p>
                      <p className="text-xs text-gray-400">Necesitas consumos reales en inventario y estimaciones pobladas en recepciones.</p>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Métricas principales */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`text-center p-3 bg-gradient-to-br ${t.bg} rounded-xl border ${t.border}`}>
                        <p className={labelCls}>Diferencia Total</p>
                        <p className={`${valuePrimaryCls} ${t.textStrong}`}>
                          {isOver ? '+' : ''}{diffTotalL.toFixed(2)} L
                        </p>
                        <p className={`text-xs ${t.text}`}>
                          {diffPct === null ? '—' : `${isOver ? '+' : ''}${diffPct.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% vs estimado`}
                        </p>
                      </div>
                      <div className={`text-center p-3 bg-gradient-to-br ${t.bg} rounded-xl border ${t.border}`}>
                        <p className={labelCls}>Desviación ml/kg</p>
                        <p className={`${valuePrimaryCls} ${t.textStrong}`}>
                          {diffMlPorKg >= 0 ? '+' : ''}{diffMlPorKg.toFixed(2)}
                        </p>
                        <p className={`text-xs ${t.text}`}>
                          {isBalanced ? 'Consumo = Estimado' : isOver ? 'Sobre-consumo' : 'Sub-consumo'}
                        </p>
                      </div>
                    </div>

                    {/* Resumen Real vs Estimado */}
                    <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className={labelCls}>Comparativa</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                          <p className="text-pink-600 dark:text-pink-400">Real (inv.)</p>
                          <p className="text-base font-bold text-pink-700 dark:text-pink-300">{realTotalL.toFixed(2)} L</p>
                        </div>
                        <div className="text-center p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                          <p className="text-teal-600 dark:text-teal-400">Estimado</p>
                          <p className="text-base font-bold text-teal-700 dark:text-teal-300">{estimadoTotalL.toFixed(2)} L</p>
                        </div>
                        <div className={`text-center p-2 bg-gradient-to-br ${t.bg} rounded-lg`}>
                          <p className={t.text}>Diferencia</p>
                          <p className={`text-base font-bold ${t.textStrong}`}>
                            {isOver ? '+' : ''}{diffTotalL.toFixed(2)} L
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Top diferencias por producto */}
                    {topDiffs.length > 0 && (
                      <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className={labelCls}>Mayores diferencias por producto</p>
                        <div className="space-y-1">
                          {topDiffs.map((d, idx) => {
                            const over = d.diffL > 0.001;
                            const under = d.diffL < -0.001;
                            const tonePerRow = over ? 'red' : under ? 'blue' : 'green';
                            const tonesPerRow = {
                              red: { dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400' },
                              blue: { dot: 'bg-blue-500', text: 'text-blue-700 dark:text-blue-400' },
                              green: { dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400' },
                            };
                            const tr = tonesPerRow[tonePerRow];
                            const sign = over ? '+' : '';
                            return (
                              <div key={d.id || idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`inline-block h-2 w-2 rounded-sm ${tr.dot}`}></span>
                                  <span className="truncate text-gray-900 dark:text-white" title={d.nombre}>{d.nombre}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="font-mono text-pink-600 dark:text-pink-400">{d.realL.toFixed(2)}L</span>
                                  <span className="text-gray-400">vs</span>
                                  <span className="font-mono text-teal-600 dark:text-teal-400">{d.estimadoL.toFixed(2)}L</span>
                                  <span className={`px-1.5 py-0.5 rounded font-semibold ${tr.text} bg-white dark:bg-gray-800 border border-current`}>
                                    {sign}{d.diffL.toFixed(2)}L
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReportsSummaryCards;
